import { Link, Navigate, useParams } from 'react-router-dom';
import { CITIES, CITY_LIST, type CitySlug } from '../lib/cities.js';
import { usePageMeta } from '../lib/usePageMeta.js';
import { LiveListingsStrip } from '../components/LiveListingsStrip.js';
import { OTTAWA_NEIGHBOURHOODS } from '../lib/neighbourhoods.js';

const SITE_ORIGIN = 'https://towshare.ca';

export function CityPage() {
  const { city } = useParams<{ city: string }>();
  const slug = (city ?? '').toLowerCase() as CitySlug;
  const data = CITIES[slug];

  if (!data) {
    return <Navigate to="/" replace />;
  }

  const canonicalPath = `/trailer-rental/${data.slug}`;
  const title = `Trailer rental ${data.name}, ON — Utility, dump, car-hauler & more`;
  const description = `Rent utility, dump, enclosed, car-hauler, landscape, snowmobile, and motorcycle trailers in ${data.name}, Ontario. Verified neighbours, $1,500 damage guarantee, Stripe-secured payments. Booked in 60 seconds.`;

  const serviceLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Trailer rental in ${data.name}, ON`,
    serviceType: 'Peer-to-peer trailer rental',
    provider: {
      '@type': 'Organization',
      name: 'TowShare',
      url: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/TowShareIcon.png`,
    },
    areaServed: {
      '@type': 'City',
      name: data.name,
      address: { '@type': 'PostalAddress', addressRegion: 'ON', addressCountry: 'CA' },
      geo: { '@type': 'GeoCoordinates', latitude: data.lat, longitude: data.lng },
    },
    url: `${SITE_ORIGIN}${canonicalPath}`,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `Popular trailer types in ${data.name}`,
      itemListElement: data.popularTypes.map((t) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: `${t.label} trailer rental` },
        url: `${SITE_ORIGIN}/search?type=${t.slug}&near=${encodeURIComponent(data.name)}`,
      })),
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'TowShare', item: SITE_ORIGIN },
      { '@type': 'ListItem', position: 2, name: 'Trailer rental', item: `${SITE_ORIGIN}/search` },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${data.name}, ON`,
        item: `${SITE_ORIGIN}${canonicalPath}`,
      },
    ],
  };

  usePageMeta({
    title,
    description,
    canonicalPath,
    keywords: `trailer rental ${data.name}, utility trailer ${data.name}, dump trailer ${data.name}, car hauler ${data.name}, peer to peer trailer ${data.name}, U-Haul alternative ${data.name}, TowShare ${data.name}`,
    jsonLd: [serviceLd, faqLd, breadcrumbLd],
  });

  const searchHref = `/search?near=${encodeURIComponent(data.name + ', ON')}`;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy via-navy-600 to-navy-700 text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, #ff751f 0, transparent 40%), radial-gradient(circle at 80% 70%, #ff751f 0, transparent 30%)',
            }}
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-24">
          <nav className="text-xs text-navy-100/80 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-orange">
              TowShare
            </Link>
            <span>/</span>
            <Link to="/search" className="hover:text-orange">
              Trailer rental
            </Link>
            <span>/</span>
            <span className="text-white">{data.name}, ON</span>
          </nav>
          <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
            Trailer rental · {data.name}, Ontario
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight max-w-3xl mb-4 leading-tight">
            Rent a trailer in {data.name}
            <br />
            <span className="text-orange">from a neighbour.</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-navy-100 max-w-2xl mb-6 md:mb-8">
            {data.tagline}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to={searchHref} className="btn-primary px-6">
              Find a trailer in {data.name}
            </Link>
            <Link
              to="/list-your-trailer"
              className="btn-ghost px-6 !text-white !border-white/30 hover:!bg-white/10"
            >
              List your trailer
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4 md:gap-6 text-sm text-navy-100">
            <div className="flex items-center gap-2">
              <Dot /> Verified renters &amp; owners
            </div>
            <div className="flex items-center gap-2">
              <Dot /> $1,500 damage guarantee
            </div>
            <div className="flex items-center gap-2">
              <Dot /> Stripe-secured payments
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR TYPES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <h2 className="text-3xl font-bold text-navy mb-2">
          Most-rented trailer types in {data.name}
        </h2>
        <p className="text-navy-600 mb-8">Tap a category to see what&rsquo;s available within 10 km.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {data.popularTypes.map((t) => (
            <Link
              key={t.slug}
              to={`/search?type=${t.slug}&near=${encodeURIComponent(data.name + ', ON')}`}
              className="card p-6 hover:border-orange hover:shadow-md transition-all group"
            >
              <div className="text-3xl mb-3">{t.emoji}</div>
              <div className="font-semibold text-navy group-hover:text-orange transition-colors">
                {t.label}
              </div>
              <div className="text-sm text-navy-500 mt-1">{t.example}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* USE CASES + WHY */}
      <section className="bg-navy-50/40 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
              What people in {data.name} rent for
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4">
              The most common trips
            </h2>
            <ul className="space-y-3 text-navy-700">
              {data.useCases.map((u) => (
                <li key={u} className="flex gap-3">
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
              Why TowShare in {data.name}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4">
              The cheaper, closer option
            </h2>
            <ul className="space-y-3 text-navy-700">
              {data.whyHere.map((u) => (
                <li key={u} className="flex gap-3">
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* LIVE LISTINGS — fresh content on every crawl */}
      <LiveListingsStrip
        lat={data.lat}
        lng={data.lng}
        radiusKm={50}
        heading={`Available now in ${data.name}`}
        emptyHint={`No active listings in ${data.name} this minute — search the whole region or browse other Eastern Ontario cities below.`}
      />

      {/* NEIGHBOURHOOD INDEX — only the cities with dedicated sub-pages get
          rich linking; others stay as chips. */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2">
          {data.slug === 'ottawa'
            ? 'Ottawa neighbourhoods — trailer rental in every pocket'
            : `Neighbourhoods we serve in and around ${data.name}`}
        </h2>
        <p className="text-navy-600 mb-6">
          TowShare matches you with owners within a 10&nbsp;km radius of your pickup point. These
          are the most common pickup areas in the {data.name} market.
        </p>
        {data.slug === 'ottawa' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
            {OTTAWA_NEIGHBOURHOODS.map((n) => (
              <Link
                key={n.slug}
                to={`/trailer-rental/${data.slug}/${n.slug}`}
                className="card p-4 hover:border-orange hover:shadow-md transition-all group"
              >
                <div className="font-semibold text-navy group-hover:text-orange transition-colors">
                  {n.name}
                </div>
                <div className="text-xs text-navy-500 mt-1 line-clamp-2">{n.tagline}</div>
              </Link>
            ))}
          </div>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {data.neighbourhoods.map((n) => (
              <li
                key={n}
                className="px-3 py-1.5 rounded-full bg-navy-50 text-navy-700 text-sm"
              >
                {n}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-navy-50/40 py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-8 text-center">
            Frequently asked — {data.name}
          </h2>
          <div className="space-y-4">
            {data.faqs.map((f) => (
              <details
                key={f.q}
                className="group card p-5 open:border-orange open:shadow-md"
              >
                <summary className="cursor-pointer font-semibold text-navy flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-orange text-xl transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-navy-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER CITIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2">
          TowShare also serves these Ontario cities
        </h2>
        <p className="text-navy-600 mb-8">Pickups within 10 km of your address in every market.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {CITY_LIST.filter((c) => c.slug !== data.slug).map((c) => (
            <Link
              key={c.slug}
              to={`/trailer-rental/${c.slug}`}
              className="card p-4 hover:border-orange hover:shadow-md transition-all"
            >
              <div className="font-semibold text-navy">{c.name}</div>
              <div className="text-xs text-navy-500 mt-1">Trailer rental →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            Ready to rent in {data.name}?
          </h2>
          <p className="text-navy-100 max-w-xl mx-auto mb-6">
            Search verified trailers within 10&nbsp;km of you. Pay through Stripe, pick up the same
            day, and you&rsquo;re done.
          </p>
          <Link to={searchHref} className="btn-primary px-8">
            Search {data.name} trailers
          </Link>
        </div>
      </section>
    </div>
  );
}

function Dot() {
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange" />;
}
